if (!customElements.get('zipcode-validator')) {
  customElements.define(
    'zipcode-validator',
    class ZipcodeValidator extends HTMLElement {
      constructor() {
        super();
        this.input = this.querySelector('input');
        this.messageContainer = this.querySelector(
          '.zipcode-validator__message',
        );
        this.spinner = this.querySelector('.loading__spinner');

        if (!this.input) return;

        this.initEvents();

        // Wait for DOM to be fully loaded to ensure the page buttons exist before enforcing state
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () =>
            this.checkStoredZip(),
          );
        } else {
          this.checkStoredZip();
        }
      }

      initEvents() {
        this.input.addEventListener('input', (e) => {
          // Allow only numbers
          this.input.value = this.input.value.replace(/\D/g, '');

          // Auto-validate when exactly 5 digits are entered
          if (this.input.value.length === 5) {
            this.validateZipCode();
          } else {
            this.clearFeedback();
          }
        });

        this.input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.validateZipCode();
          }
        });

      }

      checkStoredZip() {
        const storedZip = sessionStorage.getItem('validated_zipcode');
        if (storedZip && storedZip.length === 5) {
          this.input.value = storedZip;
          this.validateZipCode();
        }
      }

      clearFeedback() {
        this.messageContainer.classList.add('hidden');
        this.messageContainer.className = 'zipcode-validator__message hidden';
        this.messageContainer.textContent = '';
        this.input.removeAttribute('aria-invalid');
        this.input.setCustomValidity('');
        this.input.classList.remove('is-valid', 'is-invalid');
      }

      setLoading(isLoading) {
        if (isLoading) {
          this.input.setAttribute('disabled', 'true');
          if (this.spinner) this.spinner.classList.remove('hidden');
        } else {
          this.input.removeAttribute('disabled');
          if (this.spinner) this.spinner.classList.add('hidden');
          this.input.focus();
        }
      }

      async getCartState() {
        const response = await fetch('/cart.js');
        return response.json();
      }

      getStateFromZip(zipcode) {
        if (!/^\d{5}$/.test(zipcode)) return '';
        const prefix = parseInt(zipcode.substring(0, 3), 10);

        // Map 3-digit zip prefix to US State abbreviation
        if (prefix >= 350 && prefix <= 369) return 'AL';
        if (prefix >= 995 && prefix <= 999) return 'AK';
        if (prefix >= 850 && prefix <= 865) return 'AZ';
        if (prefix >= 716 && prefix <= 729) return 'AR';
        if (
          (prefix >= 900 && prefix <= 966) ||
          (prefix >= 961 && prefix <= 961)
        )
          return 'CA';
        if (prefix >= 800 && prefix <= 816) return 'CO';
        if (prefix >= 60 && prefix <= 69) return 'CT';
        if (prefix >= 197 && prefix <= 199) return 'DE';
        if (
          prefix === 200 ||
          (prefix >= 202 && prefix <= 205) ||
          prefix === 569
        )
          return 'DC';
        if (prefix >= 320 && prefix <= 349) return 'FL';
        if (prefix >= 300 && prefix <= 319) return 'GA';
        if (prefix >= 967 && prefix <= 968) return 'HI';
        if (prefix >= 832 && prefix <= 838) return 'ID';
        if (prefix >= 600 && prefix <= 629) return 'IL';
        if (prefix >= 460 && prefix <= 479) return 'IN';
        if (prefix >= 500 && prefix <= 528) return 'IA';
        if (prefix >= 660 && prefix <= 679) return 'KS';
        if (prefix >= 400 && prefix <= 427) return 'KY';
        if (prefix >= 700 && prefix <= 714) return 'LA';
        if (prefix >= 39 && prefix <= 49) return 'ME';
        if (prefix >= 206 && prefix <= 219) return 'MD';
        if (prefix >= 10 && prefix <= 27) return 'MA';
        if (prefix >= 480 && prefix <= 499) return 'MI';
        if (prefix >= 550 && prefix <= 567) return 'MN';
        if (prefix >= 386 && prefix <= 397) return 'MS';
        if (prefix >= 630 && prefix <= 658) return 'MO';
        if (prefix >= 590 && prefix <= 599) return 'MT';
        if (prefix >= 680 && prefix <= 693) return 'NE';
        if (prefix >= 890 && prefix <= 898) return 'NV';
        if (prefix >= 30 && prefix <= 38) return 'NH';
        if (prefix >= 70 && prefix <= 89) return 'NJ';
        if (prefix >= 870 && prefix <= 884) return 'NM';
        if (prefix >= 100 && prefix <= 149) return 'NY';
        if (prefix >= 270 && prefix <= 289) return 'NC';
        if (prefix >= 580 && prefix <= 588) return 'ND';
        if (prefix >= 430 && prefix <= 459) return 'OH';
        if (prefix >= 730 && prefix <= 749) return 'OK';
        if (prefix >= 970 && prefix <= 979) return 'OR';
        if (prefix >= 150 && prefix <= 196) return 'PA';
        if (prefix >= 28 && prefix <= 29) return 'RI';
        if (prefix >= 290 && prefix <= 299) return 'SC';
        if (prefix >= 570 && prefix <= 577) return 'SD';
        if (prefix >= 370 && prefix <= 385) return 'TN';
        if (
          (prefix >= 750 && prefix <= 799) ||
          (prefix >= 885 && prefix <= 885)
        )
          return 'TX';
        if (prefix >= 840 && prefix <= 847) return 'UT';
        if (prefix >= 50 && prefix <= 59) return 'VT';
        if (prefix >= 220 && prefix <= 246) return 'VA';
        if (prefix >= 980 && prefix <= 994) return 'WA';
        if (prefix >= 247 && prefix <= 268) return 'WV';
        if (prefix >= 530 && prefix <= 549) return 'WI';
        if (prefix >= 820 && prefix <= 831) return 'WY';

        // Fallbacks based on first digit
        const firstDigit = zipcode.charAt(0);
        if (firstDigit === '0') return 'NJ';
        if (firstDigit === '1') return 'NY';
        return 'NY';
      }

      async getFallbackVariantId() {
        // Try standard active/available variant
        const metaVariant =
          window.meta?.product?.variants?.find((v) => v.available) ||
          window.ShopifyAnalytics?.meta?.product?.variants?.find(
            (v) => v.available,
          );
        if (metaVariant) return metaVariant.id;

        const variantIdOnPage =
          document.querySelector('[name="id"]')?.value ||
          document.querySelector('#SelectedVariantId')?.value ||
          document.querySelector('[data-variant-id]')?.dataset.variantId;

        if (variantIdOnPage) return variantIdOnPage;

        // Fetch products and find the first available variant
        try {
          const response = await fetch('/products.json?limit=5');
          const data = await response.json();
          if (data?.products) {
            for (const product of data.products) {
              const availableVariant = product.variants?.find(
                (v) => v.available !== false,
              );
              if (availableVariant) {
                return availableVariant.id;
              }
            }
          }
        } catch (e) {
          console.error('Error fetching fallback variant:', e);
        }
        return null;
      }

      async validateZipCode() {
        const zipcode = this.input.value.trim();
        if (!/^\d{5}$/.test(zipcode)) {
          const invalidMsg =
            this.getAttribute('data-invalid-zip-message') ||
            'Please enter a valid 5-digit zip code.';
          this.showError(invalidMsg);
          return;
        }

        this.setLoading(true);
        this.clearFeedback();

        let addedTempItem = false;
        let tempVariantId = null;

        const cartUrl = window.routes?.cart_url || '/cart';
        let cartAddUrl = window.routes?.cart_add_url || '/cart/add.js';
        if (!cartAddUrl.endsWith('.js')) {
          cartAddUrl = cartAddUrl.split('?')[0].replace(/\/$/, '') + '.js';
        }
        const cartClearUrl = cartUrl.endsWith('/')
          ? `${cartUrl}clear.js`
          : `${cartUrl}/clear.js`;
        const prepareUrl = cartUrl.endsWith('/')
          ? `${cartUrl}prepare_shipping_rates.json`
          : `${cartUrl}/prepare_shipping_rates.json`;
        const asyncUrl = cartUrl.endsWith('/')
          ? `${cartUrl}async_shipping_rates.json`
          : `${cartUrl}/async_shipping_rates.json`;

        try {
          // 1. Get current cart state
          const cart = await this.getCartState();

          // 2. If cart is empty, add a temporary item to allow shipping rate calculation
          if (cart.item_count === 0) {
            tempVariantId = await this.getFallbackVariantId();
            if (!tempVariantId) {
              throw new Error(
                'No product variant available to calculate shipping rates.',
              );
            }

            const addRes = await fetch(cartAddUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: tempVariantId, quantity: 1 }),
            });
            if (!addRes.ok) {
              throw new Error('Failed to add temporary item to cart.');
            }
            await addRes.json();
            addedTempItem = true;

            // Wait 200ms to allow Shopify session cache to update
            await new Promise((resolve) => setTimeout(resolve, 200));
          }

          // 3. Resolve the state abbreviation based on zipcode
          const province = this.getStateFromZip(zipcode);

          // 4. Prepare shipping rates with the entered zipcode (use JSON payload with resolved province)
          const prepareRes = await fetch(prepareUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              shipping_address: {
                zip: zipcode,
                country: 'United States',
                province: province,
              },
            }),
          });
          if (!prepareRes.ok) {
            throw new Error('Failed to prepare shipping rates.');
          }

          // 5. Poll shipping rates (every 100ms, up to 20 times)
          let rates = [];
          for (let i = 0; i < 20; i++) {
            await new Promise((resolve) => setTimeout(resolve, 100));
            const ratesResponse = await fetch(asyncUrl);

            if (ratesResponse.status === 200) {
              const data = await ratesResponse.json();
              rates = data.shipping_rates || [];
              break;
            } else if (ratesResponse.status !== 202) {
              // If status is not 200 or 202 (pending), break early (e.g. 400, 422)
              console.warn(
                'async_shipping_rates responded with status:',
                ratesResponse.status,
              );
              break;
            }
          }

          // 6. Clean up the cart if we added a temporary item
          if (addedTempItem) {
            await fetch(cartClearUrl, { method: 'POST' });

            // Trigger cart refresh events if theme supports it
            if (window.publish && window.PUB_SUB_EVENTS) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'zipcode-validator',
              });
            }
          }

          // 7. Check if "Local Delivery" is returned as an available rate option
          const hasLocalDelivery = rates.some((rate) => {
            const name = (rate.name || '').toLowerCase();
            const code = (rate.code || '').toLowerCase();
            return (
              name.includes('local delivery') ||
              name.includes('delivery') ||
              code.includes('local delivery') ||
              code.includes('delivery') ||
              name.includes('courier') ||
              name.includes('hand')
            );
          });

          if (hasLocalDelivery) {
            this.showSuccess(zipcode);
          } else {
            this.showWarning(zipcode);
          }
        } catch (error) {
          console.error('Local delivery check failed:', error);
          const errorMsg =
            this.getAttribute('data-error-message') ||
            'Could not verify delivery availability. Please try again.';
          this.showError(errorMsg);

          // Cleanup cart in case of errors
          if (addedTempItem) {
            try {
              await fetch(cartClearUrl, { method: 'POST' });
            } catch (clearErr) {
              console.error('Error clearing cart:', clearErr);
            }
          }
        } finally {
          this.setLoading(false);
        }
      }

      showSuccess(zipcode) {
        sessionStorage.setItem('validated_zipcode', zipcode);
        const successTemplate =
          this.getAttribute('data-success-message') ||
          'Great news! We deliver to [zipcode].';
        this.messageContainer.textContent = successTemplate.replace(
          '[zipcode]',
          zipcode,
        );
        this.messageContainer.className = 'zipcode-validator__message success';
        this.messageContainer.classList.remove('hidden');
        this.input.removeAttribute('aria-invalid');
        this.input.setCustomValidity('');
        this.input.classList.add('is-valid');
        this.input.classList.remove('is-invalid');

        // Dispatch success event
        this.dispatchEvent(
          new CustomEvent('zipcode:valid', {
            bubbles: true,
            detail: { zipcode },
          }),
        );
      }

      showWarning(zipcode) {
        sessionStorage.removeItem('validated_zipcode');
        const warningTemplate =
          this.getAttribute('data-warning-message') ||
          "We're sorry, [zipcode] is outside our local delivery radius.";
        this.messageContainer.textContent = warningTemplate.replace(
          '[zipcode]',
          zipcode,
        );
        this.messageContainer.className = 'zipcode-validator__message warning';
        this.messageContainer.classList.remove('hidden');
        this.input.setAttribute('aria-invalid', 'true');
        this.input.setCustomValidity(
          'Delivery not available for this zip code',
        );
        this.input.classList.add('is-invalid');
        this.input.classList.remove('is-valid');

        // Dispatch invalid event
        this.dispatchEvent(
          new CustomEvent('zipcode:invalid', {
            bubbles: true,
            detail: { zipcode },
          }),
        );
      }

      showError(message) {
        sessionStorage.removeItem('validated_zipcode');
        this.messageContainer.textContent = message;
        this.messageContainer.className = 'zipcode-validator__message error';
        this.messageContainer.classList.remove('hidden');
        this.input.setAttribute('aria-invalid', 'true');
        this.input.setCustomValidity(message);
        this.input.classList.add('is-invalid');
        this.input.classList.remove('is-valid');
      }
    },
  );
}
